<template>
  <b-modal
      v-model="settingModal"
      :title="$t('settingModalNew.title')"
      size="xxl"
      hide-footer
      hide-header
      no-close-on-backdrop
  >
    <div class="popup-farm-setting">
      <div class="card card-custom card-stretch gutter-b">
        <!--begin::Header-->
        <div class="card-header border-0 align-items-center">
          <h3 class="card-title align-items-start flex-column">
          <span class="title font-weight-bold">
            {{ connectNm }}
          </span>
          </h3>
          <div class="card-toolbar toolbar-symbol">
            <b-button variant="dark" class="btn-icon rounded-lg" size="sm" @click="settingModal=false">
              <i class="la la-close font-size-h5"></i>
            </b-button>
          </div>
        </div>
        <!--end::Header-->
        <!--begin::Body-->
        <div class="card-body pt-0">
          <!-- 탭 네비게이션 -->
          <div class="tabs-wrapper">
            <div class="tabs-scroll-container">
              <div class="tabs-navigation">
                <button
                  class="tab-button"
                  :class="{ active: activeTab === 'environment' }"
                  @click="activeTab = 'environment'"
                >
                  {{ $t('settingModalNew.tabEnv') }}
                </button>
                <button
                  class="tab-button"
                  :class="{ active: activeTab === 'nutrient' }"
                  @click="activeTab = 'nutrient'"
                >
                  {{ $t('settingModalNew.tabNutrient') }}
                </button>
                <button
                  class="tab-button"
                  :class="{ active: activeTab === 'parameter' }"
                  @click="activeTab = 'parameter'"
                >
                  {{ $t('settingModalNew.tabParam') }}
                </button>
              </div>
            </div>
          </div>

          <!-- 탭 컨텐츠 -->
          <div class="tab-content-area">
            <!-- 재배실 환경 탭 -->
            <div v-show="activeTab === 'environment'" class="tab-panel">
              <div class="settings-grid">
                <!-- LED(광주기) -->
                <div class="setting-card">
                  <div class="setting-header">
                    <h4 class="setting-title">{{ $t('settingModalNew.led') }}</h4>
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      @click="saveLedSetting"
                      :disabled="loadingStates.led"
                    >
                      <span v-if="loadingStates.led" class="spinner-border spinner-border-sm me-1"></span>
                      {{ $t('settingModalNew.save') }}
                    </button>
                  </div>
                  <div class="setting-body">
                    <div class="time-range-group">
                      <div class="time-input-group">
                        <label class="time-label">{{ $t('settingModalNew.startTime') }}</label>
                        <div class="time-inputs">
                          <input
                            type="number"
                            v-model.number="led.ledStarttimeHh"
                            min="0"
                            max="24"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateHour($event, 'led', 'ledStarttimeHh')"
                          />
                          <span class="time-separator">:</span>
                          <input
                            type="number"
                            v-model.number="led.ledStarttimeMm"
                            min="0"
                            max="59"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateMinute($event, 'led', 'ledStarttimeMm')"
                          />
                        </div>
                      </div>
                      <span class="range-separator">~</span>
                      <div class="time-input-group">
                        <label class="time-label">{{ $t('settingModalNew.endTime') }}</label>
                        <div class="time-inputs">
                          <input
                            type="number"
                            v-model.number="led.ledEndtimeHh"
                            min="0"
                            max="24"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateHour($event, 'led', 'ledEndtimeHh')"
                          />
                          <span class="time-separator">:</span>
                          <input
                            type="number"
                            v-model.number="led.ledEndtimeMm"
                            min="0"
                            max="59"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateMinute($event, 'led', 'ledEndtimeMm')"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="duration-group">
                      <label class="duration-label">{{ $t('settingModalNew.duration') }}</label>
                      <input
                        type="text"
                        :value="ledDuration"
                        readonly
                        class="form-control"
                        :placeholder="$t('settingModalNew.durationPlaceholder')"
                      />
                    </div>
                  </div>
                </div>

                <!-- 소형팬 -->
                <div class="setting-card">
                  <div class="setting-header">
                    <h4 class="setting-title">{{ $t('settingModalNew.fan') }}</h4>
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      @click="saveFanSetting"
                      :disabled="loadingStates.fan"
                    >
                      <span v-if="loadingStates.fan" class="spinner-border spinner-border-sm me-1"></span>
                      {{ $t('settingModalNew.save') }}
                    </button>
                  </div>
                  <div class="setting-body">
                    <div class="time-range-group">
                      <div class="time-input-group">
                        <label class="time-label">{{ $t('settingModalNew.startTime') }}</label>
                        <div class="time-inputs">
                          <input
                            type="number"
                            v-model.number="fan.fanStarttimeHh"
                            min="0"
                            max="24"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateHour($event, 'fan', 'fanStarttimeHh')"
                          />
                          <span class="time-separator">:</span>
                          <input
                            type="number"
                            v-model.number="fan.fanStarttimeMm"
                            min="0"
                            max="59"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateMinute($event, 'fan', 'fanStarttimeMm')"
                          />
                        </div>
                      </div>
                      <span class="range-separator">~</span>
                      <div class="time-input-group">
                        <label class="time-label">{{ $t('settingModalNew.endTime') }}</label>
                        <div class="time-inputs">
                          <input
                            type="number"
                            v-model.number="fan.fanEndtimeHh"
                            min="0"
                            max="24"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateHour($event, 'fan', 'fanEndtimeHh')"
                          />
                          <span class="time-separator">:</span>
                          <input
                            type="number"
                            v-model.number="fan.fanEndtimeMm"
                            min="0"
                            max="59"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateMinute($event, 'fan', 'fanEndtimeMm')"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="duration-group">
                      <label class="duration-label">{{ $t('settingModalNew.duration') }}</label>
                      <input
                        type="text"
                        :value="fanDuration"
                        readonly
                        class="form-control"
                        :placeholder="$t('settingModalNew.durationPlaceholder')"
                      />
                    </div>
                  </div>
                </div>

                <!-- 써큘레이터 -->
                <div class="setting-card">
                  <div class="setting-header">
                    <h4 class="setting-title">{{ $t('settingModalNew.airfan') }}</h4>
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      @click="saveAirfanSetting"
                      :disabled="loadingStates.airfan"
                    >
                      <span v-if="loadingStates.airfan" class="spinner-border spinner-border-sm me-1"></span>
                      {{ $t('settingModalNew.save') }}
                    </button>
                  </div>
                  <div class="setting-body">
                    <div class="time-range-group">
                      <div class="time-input-group">
                        <label class="time-label">{{ $t('settingModalNew.startTime') }}</label>
                        <div class="time-inputs">
                          <input
                            type="number"
                            v-model.number="airfan.airfanStarttimeHh"
                            min="0"
                            max="24"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateHour($event, 'airfan', 'airfanStarttimeHh')"
                          />
                          <span class="time-separator">:</span>
                          <input
                            type="number"
                            v-model.number="airfan.airfanStarttimeMm"
                            min="0"
                            max="59"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateMinute($event, 'airfan', 'airfanStarttimeMm')"
                          />
                        </div>
                      </div>
                      <span class="range-separator">~</span>
                      <div class="time-input-group">
                        <label class="time-label">{{ $t('settingModalNew.endTime') }}</label>
                        <div class="time-inputs">
                          <input
                            type="number"
                            v-model.number="airfan.airfanEndtimeHh"
                            min="0"
                            max="24"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateHour($event, 'airfan', 'airfanEndtimeHh')"
                          />
                          <span class="time-separator">:</span>
                          <input
                            type="number"
                            v-model.number="airfan.airfanEndtimeMm"
                            min="0"
                            max="59"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateMinute($event, 'airfan', 'airfanEndtimeMm')"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="duration-group">
                      <label class="duration-label">{{ $t('settingModalNew.duration') }}</label>
                      <input
                        type="text"
                        :value="airfanDuration"
                        readonly
                        class="form-control"
                        :placeholder="$t('settingModalNew.durationPlaceholder')"
                      />
                    </div>
                  </div>
                </div>

                <!-- CO2 -->
                <div class="setting-card">
                  <div class="setting-header">
                    <h4 class="setting-title">CO2</h4>
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      @click="saveCo2Setting"
                      :disabled="loadingStates.co2"
                    >
                      <span v-if="loadingStates.co2" class="spinner-border spinner-border-sm me-1"></span>
                      {{ $t('settingModalNew.save') }}
                    </button>
                  </div>
                  <div class="setting-body">
                    <div class="time-range-group">
                      <div class="time-input-group">
                        <label class="time-label">{{ $t('settingModalNew.startTime') }}</label>
                        <div class="time-inputs">
                          <input
                            type="number"
                            v-model.number="co2.co2StarttimeHh"
                            min="0"
                            max="24"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateHour($event, 'co2', 'co2StarttimeHh')"
                          />
                          <span class="time-separator">:</span>
                          <input
                            type="number"
                            v-model.number="co2.co2StarttimeMm"
                            min="0"
                            max="59"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateMinute($event, 'co2', 'co2StarttimeMm')"
                          />
                        </div>
                      </div>
                      <span class="range-separator">~</span>
                      <div class="time-input-group">
                        <label class="time-label">{{ $t('settingModalNew.endTime') }}</label>
                        <div class="time-inputs">
                          <input
                            type="number"
                            v-model.number="co2.co2EndtimeHh"
                            min="0"
                            max="24"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateHour($event, 'co2', 'co2EndtimeHh')"
                          />
                          <span class="time-separator">:</span>
                          <input
                            type="number"
                            v-model.number="co2.co2EndtimeMm"
                            min="0"
                            max="59"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateMinute($event, 'co2', 'co2EndtimeMm')"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="duration-group">
                      <label class="duration-label">{{ $t('settingModalNew.duration') }}</label>
                      <input
                        type="text"
                        :value="co2Duration"
                        readonly
                        class="form-control"
                        :placeholder="$t('settingModalNew.durationPlaceholder')"
                      />
                    </div>
                    <div class="value-group">
                      <label class="value-label">{{ $t('settingModalNew.targetPpm') }}</label>
                      <input
                        type="number"
                        v-model.number="co2.co2Value"
                        min="0"
                        class="form-control"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 양액설정 탭 -->
            <div v-show="activeTab === 'nutrient'" class="tab-panel">
              <div class="settings-grid">
                <!-- PH -->
                <div class="setting-card">
                  <div class="setting-header">
                    <h4 class="setting-title">PH</h4>
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      @click="savePhSetting"
                      :disabled="loadingStates.ph"
                    >
                      <span v-if="loadingStates.ph" class="spinner-border spinner-border-sm me-1"></span>
                      {{ $t('settingModalNew.save') }}
                    </button>
                  </div>
                  <div class="setting-body">
                    <div class="value-row">
                      <div class="value-group">
                        <label class="value-label">{{ $t('settingModalNew.currentValue') }}</label>
                        <input
                          type="number"
                          :value="ph.currentValue"
                          readonly
                          class="form-control"
                        />
                      </div>
                      <div class="value-group">
                        <label class="value-label">{{ $t('settingModalNew.targetValue') }}</label>
                        <input
                          type="number"
                          v-model.number="ph.phValue"
                          min="0"
                          step="0.1"
                          class="form-control"
                          placeholder="0.0"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- EC -->
                <div class="setting-card">
                  <div class="setting-header">
                    <h4 class="setting-title">EC</h4>
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      @click="saveEcSetting"
                      :disabled="loadingStates.ec"
                    >
                      <span v-if="loadingStates.ec" class="spinner-border spinner-border-sm me-1"></span>
                      {{ $t('settingModalNew.save') }}
                    </button>
                  </div>
                  <div class="setting-body">
                    <div class="value-row">
                      <div class="value-group">
                        <label class="value-label">{{ $t('settingModalNew.currentValue') }}</label>
                        <input
                          type="number"
                          :value="ec.currentValue"
                          readonly
                          class="form-control"
                        />
                      </div>
                      <div class="value-group">
                        <label class="value-label">{{ $t('settingModalNew.targetValue') }}</label>
                        <input
                          type="number"
                          v-model.number="ec.ecValue"
                          min="0"
                          step="0.1"
                          class="form-control"
                          placeholder="0.0"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- UV램프 -->
                <div class="setting-card">
                  <div class="setting-header">
                    <h4 class="setting-title">{{ $t('settingModalNew.uv') }}</h4>
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      @click="saveUvSetting"
                      :disabled="loadingStates.uv"
                    >
                      <span v-if="loadingStates.uv" class="spinner-border spinner-border-sm me-1"></span>
                      {{ $t('settingModalNew.save') }}
                    </button>
                  </div>
                  <div class="setting-body">
                    <div class="time-range-group">
                      <div class="time-input-group">
                        <label class="time-label">{{ $t('settingModalNew.startTime') }}</label>
                        <div class="time-inputs">
                          <input
                            type="number"
                            v-model.number="uv.uvStarttimeHh"
                            min="0"
                            max="24"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateHour($event, 'uv', 'uvStarttimeHh')"
                          />
                          <span class="time-separator">:</span>
                          <input
                            type="number"
                            v-model.number="uv.uvStarttimeMm"
                            min="0"
                            max="59"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateMinute($event, 'uv', 'uvStarttimeMm')"
                          />
                        </div>
                      </div>
                      <span class="range-separator">~</span>
                      <div class="time-input-group">
                        <label class="time-label">{{ $t('settingModalNew.endTime') }}</label>
                        <div class="time-inputs">
                          <input
                            type="number"
                            v-model.number="uv.uvEndtimeHh"
                            min="0"
                            max="24"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateHour($event, 'uv', 'uvEndtimeHh')"
                          />
                          <span class="time-separator">:</span>
                          <input
                            type="number"
                            v-model.number="uv.uvEndtimeMm"
                            min="0"
                            max="59"
                            class="form-control time-input"
                            placeholder="00"
                            @input="validateMinute($event, 'uv', 'uvEndtimeMm')"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="duration-group">
                      <label class="duration-label">{{ $t('settingModalNew.duration') }}</label>
                      <input
                        type="text"
                        :value="uvDuration"
                        readonly
                        class="form-control"
                        :placeholder="$t('settingModalNew.durationPlaceholder')"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 파라미터 설정 탭 -->
            <div v-show="activeTab === 'parameter'" class="tab-panel">
              <div class="settings-grid">
                <!-- EC 편차 -->
                <div class="setting-card">
                  <div class="setting-header">
                    <h4 class="setting-title">EC</h4>
                    <div class="header-input-group">
                      <label class="header-label">{{ $t('settingModalNew.deviation') }}</label>
                      <input
                        type="number"
                        v-model.number="ecParam.ecDiff"
                        min="0"
                        step="0.1"
                        class="form-control header-input"
                        placeholder="0.0"
                      />
                      <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        @click="saveEcDiffSetting"
                        :disabled="loadingStates.ecDiff"
                      >
                        <span v-if="loadingStates.ecDiff" class="spinner-border spinner-border-sm me-1"></span>
                        저장
                      </button>
                    </div>
                  </div>
                  <div class="setting-body">
                    <div class="interval-group">
                      <div class="interval-item">
                        <label class="interval-label">{{ $t('settingModalNew.injection') }}</label>
                        <input
                          type="number"
                          v-model.number="ecParam.ecDuration"
                          min="0"
                          class="form-control"
                          placeholder="0"
                        />
                      </div>
                      <div class="interval-item">
                        <label class="interval-label">{{ $t('settingModalNew.stop') }}</label>
                        <input
                          type="number"
                          v-model.number="ecParam.ecInterval"
                          min="0"
                          class="form-control"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- pH 편차 -->
                <div class="setting-card">
                  <div class="setting-header">
                    <h4 class="setting-title">pH</h4>
                    <div class="header-input-group">
                      <label class="header-label">{{ $t('settingModalNew.deviation') }}</label>
                      <input
                        type="number"
                        v-model.number="phParam.phDiff"
                        min="0"
                        step="0.1"
                        class="form-control header-input"
                        placeholder="0.0"
                      />
                      <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        @click="savePhDiffSetting"
                        :disabled="loadingStates.phDiff"
                      >
                        <span v-if="loadingStates.phDiff" class="spinner-border spinner-border-sm me-1"></span>
                        저장
                      </button>
                    </div>
                  </div>
                  <div class="setting-body">
                    <div class="interval-group">
                      <div class="interval-item">
                        <label class="interval-label">{{ $t('settingModalNew.injection') }}</label>
                        <input
                          type="number"
                          v-model.number="phParam.phDuration"
                          min="0"
                          class="form-control"
                          placeholder="0"
                        />
                      </div>
                      <div class="interval-item">
                        <label class="interval-label">{{ $t('settingModalNew.stop') }}</label>
                        <input
                          type="number"
                          v-model.number="phParam.phInterval"
                          min="0"
                          class="form-control"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- CO2 편차 -->
                <div class="setting-card">
                  <div class="setting-header">
                    <h4 class="setting-title">CO2</h4>
                    <div class="header-input-group">
                      <label class="header-label">{{ $t('settingModalNew.deviation') }}</label>
                      <input
                        type="number"
                        v-model.number="co2Param.co2Diff"
                        min="0"
                        class="form-control header-input"
                        placeholder="0"
                      />
                      <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        @click="saveCo2DiffSetting"
                        :disabled="loadingStates.co2Diff"
                      >
                        <span v-if="loadingStates.co2Diff" class="spinner-border spinner-border-sm me-1"></span>
                        저장
                      </button>
                    </div>
                  </div>
                  <div class="setting-body">
                    <div class="interval-group">
                      <div class="interval-item">
                        <label class="interval-label">{{ $t('settingModalNew.injection') }}</label>
                        <input
                          type="number"
                          v-model.number="co2Param.co2Duration"
                          min="0"
                          class="form-control"
                          placeholder="0"
                        />
                      </div>
                      <div class="interval-item">
                        <label class="interval-label">{{ $t('settingModalNew.stop') }}</label>
                        <input
                          type="number"
                          v-model.number="co2Param.co2Interval"
                          min="0"
                          class="form-control"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 소형팬 -->
                <div class="setting-card">
                  <div class="setting-header">
                    <h4 class="setting-title">{{ $t('settingModalNew.fan') }}</h4>
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      @click="saveFanParamSetting"
                      :disabled="loadingStates.fanParam"
                    >
                      <span v-if="loadingStates.fanParam" class="spinner-border spinner-border-sm me-1"></span>
                      {{ $t('settingModalNew.save') }}
                    </button>
                  </div>
                  <div class="setting-body">
                    <div class="interval-group">
                      <div class="interval-item">
                        <label class="interval-label">{{ $t('settingModalNew.operation') }}</label>
                        <input
                          type="number"
                          v-model.number="fanParam.fanDuration"
                          min="0"
                          class="form-control"
                          placeholder="0"
                        />
                      </div>
                      <div class="interval-item">
                        <label class="interval-label">{{ $t('settingModalNew.stop') }}</label>
                        <input
                          type="number"
                          v-model.number="fanParam.fanInterval"
                          min="0"
                          class="form-control"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 온도 편차 -->
                <div class="setting-card">
                  <div class="setting-header">
                    <h4 class="setting-title">{{ $t('settingModalNew.temp') }}</h4>
                    <div class="header-input-group">
                      <label class="header-label">{{ $t('settingModalNew.deviation') }}</label>
                      <input
                        type="number"
                        v-model.number="tempParam.tempDiff"
                        min="0"
                        step="0.1"
                        class="form-control header-input"
                        placeholder="0.0"
                      />
                      <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        @click="saveTempDiffSetting"
                        :disabled="loadingStates.tempDiff"
                      >
                        <span v-if="loadingStates.tempDiff" class="spinner-border spinner-border-sm me-1"></span>
                        저장
                      </button>
                    </div>
                  </div>
                </div>

                <!-- 습도 편차 -->
                <div class="setting-card">
                  <div class="setting-header">
                    <h4 class="setting-title">{{ $t('settingModalNew.humidity') }}</h4>
                    <div class="header-input-group">
                      <label class="header-label">{{ $t('settingModalNew.deviation') }}</label>
                      <input
                        type="number"
                        v-model.number="humidityParam.humidityDiff"
                        min="0"
                        class="form-control header-input"
                        placeholder="0"
                      />
                      <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        @click="saveHumidityDiffSetting"
                        :disabled="loadingStates.humidityDiff"
                      >
                        <span v-if="loadingStates.humidityDiff" class="spinner-border spinner-border-sm me-1"></span>
                        저장
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card-footer">
          <button type="button" class="btn btn-secondary btn-lg" @click="settingModal=false">
            <span>{{ $t('cultivationEnvControlSetting.close') }}</span>
          </button>
        </div>
        <!--end::Body-->
      </div>
    </div>
  </b-modal>
</template>

<script>
import {ACT_GET_CONNECT_SET, ACT_GET_LAST_STATUS_LOG, ACT_SET_CONNECT_TIME, ACT_SET_CONNECT_TIME_GOAL, ACT_SET_CONNECT_GOAL, ACT_SET_DEVIATION_INJECTION, ACT_SET_INJECTION, ACT_SET_DEVIATION} from '@/core/services/variable';
import {getItem, lengthCheck} from '@/core/services/funcs';
import {ACT_ADD_ALERT} from '@/core/services/store/alert.module';

export default {
  name: "SettingModalNew",
  props: {
    target: Number,
    systemId: Number,
    connectNm: String,
    controllerType: String,
    cropEnvType: String,
  },
  watch: {
    target(){
      this.settingModal = true;
      this.loading = false;

      Promise.all([
        this.$store.dispatch(ACT_GET_CONNECT_SET, this.systemId),
        this.$store.dispatch(ACT_GET_LAST_STATUS_LOG, this.systemId)
      ]).then(([resp, lastLog]) => {
        if(lengthCheck(lastLog)) {
          const lateStatusLog = getItem(lastLog)
          this.ph.currentValue = (lateStatusLog.phValue/100).toFixed(2);
          this.ec.currentValue = (lateStatusLog.ecValue/1000).toFixed(2);
        }

        if(lengthCheck(resp)){
          const item = getItem(resp);

          // LED 설정 - startTime + duration으로 endTime 계산
          this.led.ledStarttimeHh = item.ledStarttimeHh || 0;
          this.led.ledStarttimeMm = item.ledStarttimeMm || 0;
          const ledDurationHh = item.ledDurationHh || 0;
          const ledDurationMm = item.ledDurationMm || 0;
          const ledEndMinutes = this.led.ledStarttimeHh * 60 + this.led.ledStarttimeMm + ledDurationHh * 60 + ledDurationMm;
          this.led.ledEndtimeHh = ledEndMinutes >= 24 * 60 ? 24 : Math.floor(ledEndMinutes / 60);
          this.led.ledEndtimeMm = ledEndMinutes >= 24 * 60 ? 0 : ledEndMinutes % 60;

          // 소형팬 설정
          this.fan.fanStarttimeHh = item.fanStarttimeHh || 0;
          this.fan.fanStarttimeMm = item.fanStarttimeMm || 0;
          const fanDurationHh = item.fanDurationHh || 0;
          const fanDurationMm = item.fanDurationMm || 0;
          const fanEndMinutes = this.fan.fanStarttimeHh * 60 + this.fan.fanStarttimeMm + fanDurationHh * 60 + fanDurationMm;
          this.fan.fanEndtimeHh = fanEndMinutes >= 24 * 60 ? 24 : Math.floor(fanEndMinutes / 60);
          this.fan.fanEndtimeMm = fanEndMinutes >= 24 * 60 ? 0 : fanEndMinutes % 60;

          // 써큘레이터 설정
          this.airfan.airfanStarttimeHh = item.airfanStarttimeHh || 0;
          this.airfan.airfanStarttimeMm = item.airfanStarttimeMm || 0;
          const airfanDurationHh = item.airfanDurationHh || 0;
          const airfanDurationMm = item.airfanDurationMm || 0;
          const airfanEndMinutes = this.airfan.airfanStarttimeHh * 60 + this.airfan.airfanStarttimeMm + airfanDurationHh * 60 + airfanDurationMm;
          this.airfan.airfanEndtimeHh = airfanEndMinutes >= 24 * 60 ? 24 : Math.floor(airfanEndMinutes / 60);
          this.airfan.airfanEndtimeMm = airfanEndMinutes >= 24 * 60 ? 0 : airfanEndMinutes % 60;

          // CO2 설정
          this.co2.co2StarttimeHh = item.co2StarttimeHh || 0;
          this.co2.co2StarttimeMm = item.co2StarttimeMm || 0;
          const co2DurationHh = item.co2DurationHh || 0;
          const co2DurationMm = item.co2DurationMm || 0;
          const co2EndMinutes = this.co2.co2StarttimeHh * 60 + this.co2.co2StarttimeMm + co2DurationHh * 60 + co2DurationMm;
          this.co2.co2EndtimeHh = co2EndMinutes >= 24 * 60 ? 24 : Math.floor(co2EndMinutes / 60);
          this.co2.co2EndtimeMm = co2EndMinutes >= 24 * 60 ? 0 : co2EndMinutes % 60;
          this.co2.co2Value = item.co2Value || 0;

          // PH 설정
          this.ph.phValue = item.phValue || 0;

          // EC 설정
          this.ec.ecValue = item.ecValue || 0;

          // UV 설정
          this.uv.uvStarttimeHh = item.uvStarttimeHh || 0;
          this.uv.uvStarttimeMm = item.uvStarttimeMm || 0;
          const uvDurationHh = item.uvDurationHh || 0;
          const uvDurationMm = item.uvDurationMm || 0;
          const uvEndMinutes = this.uv.uvStarttimeHh * 60 + this.uv.uvStarttimeMm + uvDurationHh * 60 + uvDurationMm;
          this.uv.uvEndtimeHh = uvEndMinutes >= 24 * 60 ? 24 : Math.floor(uvEndMinutes / 60);
          this.uv.uvEndtimeMm = uvEndMinutes >= 24 * 60 ? 0 : uvEndMinutes % 60;

          // 파라미터 설정
          this.ecParam.ecDiff = item.ecDiff || 0;
          this.ecParam.ecDuration = item.ecInjection || 0;
          this.ecParam.ecInterval = item.ecStop || 0;

          this.phParam.phDiff = item.phDiff || 0;
          this.phParam.phDuration = item.phInjection || 0;
          this.phParam.phInterval = item.phStop || 0;

          this.co2Param.co2Diff = item.co2Diff || 0;
          this.co2Param.co2Duration = item.co2Injection || 0;
          this.co2Param.co2Interval = item.co2Stop || 0;

          this.fanParam.fanDuration = item.fanInjection || 0;
          this.fanParam.fanInterval = item.fanStop || 0;

          this.tempParam.tempDiff = item.tempDiff || 0;
          this.humidityParam.humidityDiff = item.humidityDiff || 0;
        }
      }).catch(e => {
        console.error(e);
      });
    }
  },
  computed: {
    ledDuration() {
      const startHour = parseInt(this.led.ledStarttimeHh) || 0;
      const startMin = parseInt(this.led.ledStarttimeMm) || 0;
      const endHour = parseInt(this.led.ledEndtimeHh) || 0;
      const endMin = parseInt(this.led.ledEndtimeMm) || 0;

      let duration = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      if(duration < 0) duration += 24 * 60;

      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      return `${hours}시간 ${minutes}분`;
    },
    fanDuration() {
      const startHour = parseInt(this.fan.fanStarttimeHh) || 0;
      const startMin = parseInt(this.fan.fanStarttimeMm) || 0;
      const endHour = parseInt(this.fan.fanEndtimeHh) || 0;
      const endMin = parseInt(this.fan.fanEndtimeMm) || 0;

      let duration = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      if(duration < 0) duration += 24 * 60;

      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      return `${hours}시간 ${minutes}분`;
    },
    airfanDuration() {
      const startHour = parseInt(this.airfan.airfanStarttimeHh) || 0;
      const startMin = parseInt(this.airfan.airfanStarttimeMm) || 0;
      const endHour = parseInt(this.airfan.airfanEndtimeHh) || 0;
      const endMin = parseInt(this.airfan.airfanEndtimeMm) || 0;

      let duration = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      if(duration < 0) duration += 24 * 60;

      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      return `${hours}시간 ${minutes}분`;
    },
    co2Duration() {
      const startHour = parseInt(this.co2.co2StarttimeHh) || 0;
      const startMin = parseInt(this.co2.co2StarttimeMm) || 0;
      const endHour = parseInt(this.co2.co2EndtimeHh) || 0;
      const endMin = parseInt(this.co2.co2EndtimeMm) || 0;

      let duration = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      if(duration < 0) duration += 24 * 60;

      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      return `${hours}시간 ${minutes}분`;
    },
    uvDuration() {
      const startHour = parseInt(this.uv.uvStarttimeHh) || 0;
      const startMin = parseInt(this.uv.uvStarttimeMm) || 0;
      const endHour = parseInt(this.uv.uvEndtimeHh) || 0;
      const endMin = parseInt(this.uv.uvEndtimeMm) || 0;

      let duration = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      if(duration < 0) duration += 24 * 60;

      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      return `${hours}시간 ${minutes}분`;
    }
  },
  data() {
    return {
      settingModal: false,
      loading: false,
      activeTab: 'environment',
      led: {
        ledStarttimeHh: '00',
        ledStarttimeMm: '00',
        ledEndtimeHh: '00',
        ledEndtimeMm: '00'
      },
      fan: {
        fanStarttimeHh: '00',
        fanStarttimeMm: '00',
        fanEndtimeHh: '00',
        fanEndtimeMm: '00'
      },
      airfan: {
        airfanStarttimeHh: '00',
        airfanStarttimeMm: '00',
        airfanEndtimeHh: '00',
        airfanEndtimeMm: '00'
      },
      co2: {
        co2StarttimeHh: 0,
        co2StarttimeMm: 0,
        co2EndtimeHh: 0,
        co2EndtimeMm: 0,
        co2Value: 0,
      },
      ph: {
        currentValue: 0,
        phValue: 0,
      },
      ec: {
        currentValue: 0,
        ecValue: 0,
      },
      uv: {
        uvStarttimeHh: '00',
        uvStarttimeMm: '00',
        uvEndtimeHh: '00',
        uvEndtimeMm: '00'
      },
      ecParam: {
        ecDiff: 0,
        ecDuration: 0,
        ecInterval: 0
      },
      phParam: {
        phDiff: 0,
        phDuration: 0,
        phInterval: 0
      },
      co2Param: {
        co2Diff: 0,
        co2Duration: 0,
        co2Interval: 0
      },
      fanParam: {
        fanDuration: 0,
        fanInterval: 0
      },
      tempParam: {
        tempDiff: 0
      },
      humidityParam: {
        humidityDiff: 0
      },
      loadingStates: {
        led: false,
        fan: false,
        airfan: false,
        co2: false,
        ph: false,
        ec: false,
        uv: false,
        ecDiff: false,
        phDiff: false,
        co2Diff: false,
        fanParam: false,
        tempDiff: false,
        humidityDiff: false
      },
      REFRESH_DELAY: 3000, // 파라미터 설정 값 재확인 대기 시간 (밀리초)
    };
  },
  methods: {
    async refreshSettings(section) {
      try {
        const resp = await this.$store.dispatch(ACT_GET_CONNECT_SET, this.systemId);
        if(lengthCheck(resp)){
          const item = getItem(resp);

          if(section === 'led') {
            this.led.ledStarttimeHh = item.ledStarttimeHh || 0;
            this.led.ledStarttimeMm = item.ledStarttimeMm || 0;
            const ledEndMinutes = this.led.ledStarttimeHh * 60 + this.led.ledStarttimeMm + (item.ledDurationHh || 0) * 60 + (item.ledDurationMm || 0);
            this.led.ledEndtimeHh = ledEndMinutes >= 24 * 60 ? 24 : Math.floor(ledEndMinutes / 60);
            this.led.ledEndtimeMm = ledEndMinutes >= 24 * 60 ? 0 : ledEndMinutes % 60;
          } else if(section === 'fan') {
            this.fan.fanStarttimeHh = item.fanStarttimeHh || 0;
            this.fan.fanStarttimeMm = item.fanStarttimeMm || 0;
            const fanEndMinutes = this.fan.fanStarttimeHh * 60 + this.fan.fanStarttimeMm + (item.fanDurationHh || 0) * 60 + (item.fanDurationMm || 0);
            this.fan.fanEndtimeHh = fanEndMinutes >= 24 * 60 ? 24 : Math.floor(fanEndMinutes / 60);
            this.fan.fanEndtimeMm = fanEndMinutes >= 24 * 60 ? 0 : fanEndMinutes % 60;
          } else if(section === 'airfan') {
            this.airfan.airfanStarttimeHh = item.airfanStarttimeHh || 0;
            this.airfan.airfanStarttimeMm = item.airfanStarttimeMm || 0;
            const airfanEndMinutes = this.airfan.airfanStarttimeHh * 60 + this.airfan.airfanStarttimeMm + (item.airfanDurationHh || 0) * 60 + (item.airfanDurationMm || 0);
            this.airfan.airfanEndtimeHh = airfanEndMinutes >= 24 * 60 ? 24 : Math.floor(airfanEndMinutes / 60);
            this.airfan.airfanEndtimeMm = airfanEndMinutes >= 24 * 60 ? 0 : airfanEndMinutes % 60;
          } else if(section === 'co2') {
            this.co2.co2StarttimeHh = item.co2StarttimeHh || 0;
            this.co2.co2StarttimeMm = item.co2StarttimeMm || 0;
            const co2EndMinutes = this.co2.co2StarttimeHh * 60 + this.co2.co2StarttimeMm + (item.co2DurationHh || 0) * 60 + (item.co2DurationMm || 0);
            this.co2.co2EndtimeHh = co2EndMinutes >= 24 * 60 ? 24 : Math.floor(co2EndMinutes / 60);
            this.co2.co2EndtimeMm = co2EndMinutes >= 24 * 60 ? 0 : co2EndMinutes % 60;
            this.co2.co2Value = item.co2Value || 0;
          } else if(section === 'ph') {
            this.ph.phValue = item.phValue || 0;
          } else if(section === 'ec') {
            this.ec.ecValue = item.ecValue || 0;
          } else if(section === 'uv') {
            this.uv.uvStarttimeHh = item.uvStarttimeHh || 0;
            this.uv.uvStarttimeMm = item.uvStarttimeMm || 0;
            const uvEndMinutes = this.uv.uvStarttimeHh * 60 + this.uv.uvStarttimeMm + (item.uvDurationHh || 0) * 60 + (item.uvDurationMm || 0);
            this.uv.uvEndtimeHh = uvEndMinutes >= 24 * 60 ? 24 : Math.floor(uvEndMinutes / 60);
            this.uv.uvEndtimeMm = uvEndMinutes >= 24 * 60 ? 0 : uvEndMinutes % 60;
          } else if(section === 'ecDiff') {
            this.ecParam.ecDiff = item.ecDiff || 0;
            this.ecParam.ecDuration = item.ecInjection || 0;
            this.ecParam.ecInterval = item.ecStop || 0;
          } else if(section === 'phDiff') {
            this.phParam.phDiff = item.phDiff || 0;
            this.phParam.phDuration = item.phInjection || 0;
            this.phParam.phInterval = item.phStop || 0;
          } else if(section === 'co2Diff') {
            this.co2Param.co2Diff = item.co2Diff || 0;
            this.co2Param.co2Duration = item.co2Injection || 0;
            this.co2Param.co2Interval = item.co2Stop || 0;
          } else if(section === 'fanParam') {
            this.fanParam.fanDuration = item.fanInjection || 0;
            this.fanParam.fanInterval = item.fanStop || 0;
          } else if(section === 'tempDiff') {
            this.tempParam.tempDiff = item.tempDiff || 0;
          } else if(section === 'humidityDiff') {
            this.humidityParam.humidityDiff = item.humidityDiff || 0;
          }
        }
      } catch(e) {
        console.error(e);
      }
    },
    validateHour(event, category, field) {
      const value = parseInt(event.target.value);
      if (value < 0 || value > 24 || isNaN(value)) {
        this[category][field] = '';
        event.target.value = '';
      } else if (value === 24) {
        const minuteField = field.replace('Hh', 'Mm');
        this[category][minuteField] = 0;
      }
    },
    validateMinute(event, category, field) {
      const value = parseInt(event.target.value);
      const hourField = field.replace('Mm', 'Hh');
      const hour = parseInt(this[category][hourField]);
      if (value < 0 || value > 59 || isNaN(value)) {
        this[category][field] = '';
        event.target.value = '';
      } else if (hour === 24 && value > 0) {
        this[category][field] = 0;
        event.target.value = '00';
      }
    },
    async saveLedSetting() {
      if(this.loadingStates.led) return;
      this.loadingStates.led = true;

      const startHour = parseInt(this.led.ledStarttimeHh) || 0;
      const startMin = parseInt(this.led.ledStarttimeMm) || 0;
      const endHour = parseInt(this.led.ledEndtimeHh) || 0;
      const endMin = parseInt(this.led.ledEndtimeMm) || 0;

      let durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      if(durationMinutes < 0) durationMinutes += 24 * 60;

      const data = {
        field: '110',
        startTimeHh: startHour,
        startTimeMm: startMin,
        endTimeHh: endHour,
        endTimeMm: endMin,
        durationHh: Math.floor(durationMinutes / 60),
        durationMm: durationMinutes % 60
      };

      try {
        const resp = await this.$store.dispatch(ACT_SET_CONNECT_TIME, {systemId: this.systemId, data});
        if(resp && resp.data && resp.data.resultCode === 200){
          this.$store.dispatch(ACT_ADD_ALERT, {message: `${this.$t('settingModalNew.led')} ${this.$t('settingModalNew.savedSuccess')}`, color: 'success'});
          setTimeout(async () => {
            await this.refreshSettings('led');
            this.loadingStates.led = false;
          }, this.REFRESH_DELAY);
        } else {
          const locale = this.$i18n.locale;
          const errorMsg = locale === 'ko' ? resp.data.resultMessageKo : resp.data.resultMessageEn;
          this.$store.dispatch(ACT_ADD_ALERT, {message: errorMsg || '저장 중 오류가 발생했습니다.'});
          this.loadingStates.led = false;
        }
      } catch(e) {
        console.error(e);
        this.$store.dispatch(ACT_ADD_ALERT, {message: '저장 중 오류가 발생했습니다.'});
        this.loadingStates.led = false;
      }
    },
    async saveFanSetting() {
      if(this.loadingStates.fan) return;
      this.loadingStates.fan = true;

      const startHour = parseInt(this.fan.fanStarttimeHh) || 0;
      const startMin = parseInt(this.fan.fanStarttimeMm) || 0;
      const endHour = parseInt(this.fan.fanEndtimeHh) || 0;
      const endMin = parseInt(this.fan.fanEndtimeMm) || 0;

      let durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      if(durationMinutes < 0) durationMinutes += 24 * 60;

      const data = {
        field: '120',
        startTimeHh: startHour,
        startTimeMm: startMin,
        endTimeHh: endHour,
        endTimeMm: endMin,
        durationHh: Math.floor(durationMinutes / 60),
        durationMm: durationMinutes % 60
      };

      try {
        const resp = await this.$store.dispatch(ACT_SET_CONNECT_TIME, {systemId: this.systemId, data});
        if(resp && resp.data && resp.data.resultCode === 200){
          this.$store.dispatch(ACT_ADD_ALERT, {message: `${this.$t('settingModalNew.fan')} ${this.$t('settingModalNew.savedSuccess')}`, color: 'success'});
          setTimeout(async () => {
            await this.refreshSettings('fan');
            this.loadingStates.fan = false;
          }, this.REFRESH_DELAY);
        } else {
          const locale = this.$i18n.locale;
          const errorMsg = locale === 'ko' ? resp.data.resultMessageKo : resp.data.resultMessageEn;
          this.$store.dispatch(ACT_ADD_ALERT, {message: errorMsg || '저장 중 오류가 발생했습니다.'});
          this.loadingStates.fan = false;
        }
      } catch(e) {
        console.error(e);
        this.$store.dispatch(ACT_ADD_ALERT, {message: '저장 중 오류가 발생했습니다.'});
        this.loadingStates.fan = false;
      }
    },
    async saveAirfanSetting() {
      if(this.loadingStates.airfan) return;
      this.loadingStates.airfan = true;

      const startHour = parseInt(this.airfan.airfanStarttimeHh) || 0;
      const startMin = parseInt(this.airfan.airfanStarttimeMm) || 0;
      const endHour = parseInt(this.airfan.airfanEndtimeHh) || 0;
      const endMin = parseInt(this.airfan.airfanEndtimeMm) || 0;

      let durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      if(durationMinutes < 0) durationMinutes += 24 * 60;

      const data = {
        field: '130',
        startTimeHh: startHour,
        startTimeMm: startMin,
        endTimeHh: endHour,
        endTimeMm: endMin,
        durationHh: Math.floor(durationMinutes / 60),
        durationMm: durationMinutes % 60
      };

      try {
        const resp = await this.$store.dispatch(ACT_SET_CONNECT_TIME, {systemId: this.systemId, data});
        if(resp && resp.data && resp.data.resultCode === 200){
          this.$store.dispatch(ACT_ADD_ALERT, {message: `${this.$t('settingModalNew.airfan')} ${this.$t('settingModalNew.savedSuccess')}`, color: 'success'});
          setTimeout(async () => {
            await this.refreshSettings('airfan');
            this.loadingStates.airfan = false;
          }, this.REFRESH_DELAY);
        } else {
          const locale = this.$i18n.locale;
          const errorMsg = locale === 'ko' ? resp.data.resultMessageKo : resp.data.resultMessageEn;
          this.$store.dispatch(ACT_ADD_ALERT, {message: errorMsg || this.$t('settingModalNew.saveError')});
          this.loadingStates.airfan = false;
        }
      } catch(e) {
        console.error(e);
        this.$store.dispatch(ACT_ADD_ALERT, {message: this.$t('settingModalNew.saveError')});
        this.loadingStates.airfan = false;
      }
    },
    async saveCo2Setting() {
      if(this.loadingStates.co2) return;
      this.loadingStates.co2 = true;

      const startHour = parseInt(this.co2.co2StarttimeHh) || 0;
      const startMin = parseInt(this.co2.co2StarttimeMm) || 0;
      const endHour = parseInt(this.co2.co2EndtimeHh) || 0;
      const endMin = parseInt(this.co2.co2EndtimeMm) || 0;

      let durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      if(durationMinutes < 0) durationMinutes += 24 * 60;

      const data = {
        field: '140',
        startTimeHh: startHour,
        startTimeMm: startMin,
        endTimeHh: endHour,
        endTimeMm: endMin,
        durationHh: Math.floor(durationMinutes / 60),
        durationMm: durationMinutes % 60,
        goal: parseInt(this.co2.co2Value) || 0
      };

      try {
        const resp = await this.$store.dispatch(ACT_SET_CONNECT_TIME_GOAL, {systemId: this.systemId, data});
        if(resp && resp.data && resp.data.resultCode === 200){
          this.$store.dispatch(ACT_ADD_ALERT, {message: `${this.$t('settingModalNew.co2')} ${this.$t('settingModalNew.savedSuccess')}`, color: 'success'});
          setTimeout(async () => {
            await this.refreshSettings('co2');
            this.loadingStates.co2 = false;
          }, this.REFRESH_DELAY);
        } else {
          const locale = this.$i18n.locale;
          const errorMsg = locale === 'ko' ? resp.data.resultMessageKo : resp.data.resultMessageEn;
          this.$store.dispatch(ACT_ADD_ALERT, {message: errorMsg || this.$t('settingModalNew.saveError')});
          this.loadingStates.co2 = false;
        }
      } catch(e) {
        console.error(e);
        this.$store.dispatch(ACT_ADD_ALERT, {message: this.$t('settingModalNew.saveError')});
        this.loadingStates.co2 = false;
      }
    },
    async savePhSetting() {
      if(this.loadingStates.ph) return;
      this.loadingStates.ph = true;

      const data = {
        field: '210',
        goal: parseFloat(this.ph.phValue) || 0
      };

      try {
        const resp = await this.$store.dispatch(ACT_SET_CONNECT_GOAL, {systemId: this.systemId, data});
        if(resp && resp.data && resp.data.resultCode === 200){
          this.$store.dispatch(ACT_ADD_ALERT, {message: `${this.$t('settingModalNew.ph')} ${this.$t('settingModalNew.savedSuccess')}`, color: 'success'});
          setTimeout(async () => {
            await this.refreshSettings('ph');
            this.loadingStates.ph = false;
          }, this.REFRESH_DELAY);
        } else {
          const locale = this.$i18n.locale;
          const errorMsg = locale === 'ko' ? resp.data.resultMessageKo : resp.data.resultMessageEn;
          this.$store.dispatch(ACT_ADD_ALERT, {message: errorMsg || this.$t('settingModalNew.saveError')});
          this.loadingStates.ph = false;
        }
      } catch(e) {
        console.error(e);
        this.$store.dispatch(ACT_ADD_ALERT, {message: this.$t('settingModalNew.saveError')});
        this.loadingStates.ph = false;
      }
    },
    async saveEcSetting() {
      if(this.loadingStates.ec) return;
      this.loadingStates.ec = true;

      const data = {
        field: '220',
        goal: parseFloat(this.ec.ecValue) || 0
      };

      try {
        const resp = await this.$store.dispatch(ACT_SET_CONNECT_GOAL, {systemId: this.systemId, data});
        if(resp && resp.data && resp.data.resultCode === 200){
          this.$store.dispatch(ACT_ADD_ALERT, {message: `${this.$t('settingModalNew.ec')} ${this.$t('settingModalNew.savedSuccess')}`, color: 'success'});
          setTimeout(async () => {
            await this.refreshSettings('ec');
            this.loadingStates.ec = false;
          }, this.REFRESH_DELAY);
        } else {
          const locale = this.$i18n.locale;
          const errorMsg = locale === 'ko' ? resp.data.resultMessageKo : resp.data.resultMessageEn;
          this.$store.dispatch(ACT_ADD_ALERT, {message: errorMsg || this.$t('settingModalNew.saveError')});
          this.loadingStates.ec = false;
        }
      } catch(e) {
        console.error(e);
        this.$store.dispatch(ACT_ADD_ALERT, {message: this.$t('settingModalNew.saveError')});
        this.loadingStates.ec = false;
      }
    },
    async saveUvSetting() {
      if(this.loadingStates.uv) return;
      this.loadingStates.uv = true;

      const startHour = parseInt(this.uv.uvStarttimeHh) || 0;
      const startMin = parseInt(this.uv.uvStarttimeMm) || 0;
      const endHour = parseInt(this.uv.uvEndtimeHh) || 0;
      const endMin = parseInt(this.uv.uvEndtimeMm) || 0;

      let durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      if(durationMinutes < 0) durationMinutes += 24 * 60;

      const data = {
        field: '230',
        startTimeHh: startHour,
        startTimeMm: startMin,
        endTimeHh: endHour,
        endTimeMm: endMin,
        durationHh: Math.floor(durationMinutes / 60),
        durationMm: durationMinutes % 60
      };

      try {
        const resp = await this.$store.dispatch(ACT_SET_CONNECT_TIME, {systemId: this.systemId, data});
        if(resp && resp.data && resp.data.resultCode === 200){
          this.$store.dispatch(ACT_ADD_ALERT, {message: `${this.$t('settingModalNew.uv')} ${this.$t('settingModalNew.savedSuccess')}`, color: 'success'});
          setTimeout(async () => {
            await this.refreshSettings('uv');
            this.loadingStates.uv = false;
          }, this.REFRESH_DELAY);
        } else {
          const locale = this.$i18n.locale;
          const errorMsg = locale === 'ko' ? resp.data.resultMessageKo : resp.data.resultMessageEn;
          this.$store.dispatch(ACT_ADD_ALERT, {message: errorMsg || this.$t('settingModalNew.saveError')});
          this.loadingStates.uv = false;
        }
      } catch(e) {
        console.error(e);
        this.$store.dispatch(ACT_ADD_ALERT, {message: this.$t('settingModalNew.saveError')});
        this.loadingStates.uv = false;
      }
    },
    async saveEcDiffSetting() {
      if(this.loadingStates.ecDiff) return;
      this.loadingStates.ecDiff = true;

      const data = {
        field: '310',
        deviation: parseFloat(this.ecParam.ecDiff) || 0,
        injection: parseInt(this.ecParam.ecDuration) || 0,
        stop: parseInt(this.ecParam.ecInterval) || 0
      };

      try {
        const resp = await this.$store.dispatch(ACT_SET_DEVIATION_INJECTION, {systemId: this.systemId, data});
        if(resp && resp.data && resp.data.resultCode === 200){
          this.$store.dispatch(ACT_ADD_ALERT, {message: `${this.$t('settingModalNew.ecDiff')} ${this.$t('settingModalNew.savedSuccess')}`, color: 'success'});

          setTimeout(async () => {
            await this.refreshSettings('ecDiff');
            this.loadingStates.ecDiff = false;
          }, this.REFRESH_DELAY);
        } else {
          const locale = this.$i18n.locale;
          const errorMsg = locale === 'ko' ? resp.data.resultMessageKo : resp.data.resultMessageEn;
          this.$store.dispatch(ACT_ADD_ALERT, {message: errorMsg || this.$t('settingModalNew.saveError')});
          this.loadingStates.ecDiff = false;
        }
      } catch(e) {
        console.error(e);
        this.$store.dispatch(ACT_ADD_ALERT, {message: this.$t('settingModalNew.saveError')});
        this.loadingStates.ecDiff = false;
      }
    },
    async savePhDiffSetting() {
      if(this.loadingStates.phDiff) return;
      this.loadingStates.phDiff = true;

      const data = {
        field: '320',
        deviation: parseFloat(this.phParam.phDiff) || 0,
        injection: parseInt(this.phParam.phDuration) || 0,
        stop: parseInt(this.phParam.phInterval) || 0
      };

      try {
        const resp = await this.$store.dispatch(ACT_SET_DEVIATION_INJECTION, {systemId: this.systemId, data});
        if(resp && resp.data && resp.data.resultCode === 200){
          this.$store.dispatch(ACT_ADD_ALERT, {message: `${this.$t('settingModalNew.phDiff')} ${this.$t('settingModalNew.savedSuccess')}`, color: 'success'});

          setTimeout(async () => {
            await this.refreshSettings('phDiff');
            this.loadingStates.phDiff = false;
          }, this.REFRESH_DELAY);
        } else {
          const locale = this.$i18n.locale;
          const errorMsg = locale === 'ko' ? resp.data.resultMessageKo : resp.data.resultMessageEn;
          this.$store.dispatch(ACT_ADD_ALERT, {message: errorMsg || this.$t('settingModalNew.saveError')});
          this.loadingStates.phDiff = false;
        }
      } catch(e) {
        console.error(e);
        this.$store.dispatch(ACT_ADD_ALERT, {message: this.$t('settingModalNew.saveError')});
        this.loadingStates.phDiff = false;
      }
    },
    async saveCo2DiffSetting() {
      if(this.loadingStates.co2Diff) return;
      this.loadingStates.co2Diff = true;

      const data = {
        field: '330',
        deviation: parseInt(this.co2Param.co2Diff) || 0,
        injection: parseInt(this.co2Param.co2Duration) || 0,
        stop: parseInt(this.co2Param.co2Interval) || 0
      };

      try {
        const resp = await this.$store.dispatch(ACT_SET_DEVIATION_INJECTION, {systemId: this.systemId, data});
        if(resp && resp.data && resp.data.resultCode === 200){
          this.$store.dispatch(ACT_ADD_ALERT, {message: `${this.$t('settingModalNew.co2Diff')} ${this.$t('settingModalNew.savedSuccess')}`, color: 'success'});

          setTimeout(async () => {
            await this.refreshSettings('co2Diff');
            this.loadingStates.co2Diff = false;
          }, this.REFRESH_DELAY);
        } else {
          const locale = this.$i18n.locale;
          const errorMsg = locale === 'ko' ? resp.data.resultMessageKo : resp.data.resultMessageEn;
          this.$store.dispatch(ACT_ADD_ALERT, {message: errorMsg || this.$t('settingModalNew.saveError')});
          this.loadingStates.co2Diff = false;
        }
      } catch(e) {
        console.error(e);
        this.$store.dispatch(ACT_ADD_ALERT, {message: this.$t('settingModalNew.saveError')});
        this.loadingStates.co2Diff = false;
      }
    },
    async saveFanParamSetting() {
      if(this.loadingStates.fanParam) return;
      this.loadingStates.fanParam = true;

      const data = {
        field: '340',
        injection: parseInt(this.fanParam.fanDuration) || 0,
        stop: parseInt(this.fanParam.fanInterval) || 0
      };

      try {
        const resp = await this.$store.dispatch(ACT_SET_INJECTION, {systemId: this.systemId, data});
        if(resp && resp.data && resp.data.resultCode === 200){
          this.$store.dispatch(ACT_ADD_ALERT, {message: `${this.$t('settingModalNew.fanParam')} ${this.$t('settingModalNew.savedSuccess')}`, color: 'success'});

          setTimeout(async () => {
            await this.refreshSettings('fanParam');
            this.loadingStates.fanParam = false;
          }, this.REFRESH_DELAY);
        } else {
          const locale = this.$i18n.locale;
          const errorMsg = locale === 'ko' ? resp.data.resultMessageKo : resp.data.resultMessageEn;
          this.$store.dispatch(ACT_ADD_ALERT, {message: errorMsg || this.$t('settingModalNew.saveError')});
          this.loadingStates.fanParam = false;
        }
      } catch(e) {
        console.error(e);
        this.$store.dispatch(ACT_ADD_ALERT, {message: this.$t('settingModalNew.saveError')});
        this.loadingStates.fanParam = false;
      }
    },
    async saveTempDiffSetting() {
      if(this.loadingStates.tempDiff) return;
      this.loadingStates.tempDiff = true;

      const data = {
        field: '350',
        deviation: parseFloat(this.tempParam.tempDiff) || 0
      };

      try {
        const resp = await this.$store.dispatch(ACT_SET_DEVIATION, {systemId: this.systemId, data});
        if(resp && resp.data && resp.data.resultCode === 200){
          this.$store.dispatch(ACT_ADD_ALERT, {message: `${this.$t('settingModalNew.temp')} ${this.$t('settingModalNew.savedSuccess')}`, color: 'success'});

          setTimeout(async () => {
            await this.refreshSettings('tempDiff');
            this.loadingStates.tempDiff = false;
          }, this.REFRESH_DELAY);
        } else {
          const locale = this.$i18n.locale;
          const errorMsg = locale === 'ko' ? resp.data.resultMessageKo : resp.data.resultMessageEn;
          this.$store.dispatch(ACT_ADD_ALERT, {message: errorMsg || this.$t('settingModalNew.saveError')});
          this.loadingStates.tempDiff = false;
        }
      } catch(e) {
        console.error(e);
        this.$store.dispatch(ACT_ADD_ALERT, {message: this.$t('settingModalNew.saveError')});
        this.loadingStates.tempDiff = false;
      }
    },
    async saveHumidityDiffSetting() {
      if(this.loadingStates.humidityDiff) return;
      this.loadingStates.humidityDiff = true;

      const data = {
        field: '360',
        deviation: parseInt(this.humidityParam.humidityDiff) || 0
      };

      try {
        const resp = await this.$store.dispatch(ACT_SET_DEVIATION, {systemId: this.systemId, data});
        if(resp && resp.data && resp.data.resultCode === 200){
          this.$store.dispatch(ACT_ADD_ALERT, {message: `${this.$t('settingModalNew.humidity')} ${this.$t('settingModalNew.savedSuccess')}`, color: 'success'});

          setTimeout(async () => {
            await this.refreshSettings('humidityDiff');
            this.loadingStates.humidityDiff = false;
          }, this.REFRESH_DELAY);
        } else {
          const locale = this.$i18n.locale;
          const errorMsg = locale === 'ko' ? resp.data.resultMessageKo : resp.data.resultMessageEn;
          this.$store.dispatch(ACT_ADD_ALERT, {message: errorMsg || this.$t('settingModalNew.saveError')});
          this.loadingStates.humidityDiff = false;
        }
      } catch(e) {
        console.error(e);
        this.$store.dispatch(ACT_ADD_ALERT, {message: this.$t('settingModalNew.saveError')});
        this.loadingStates.humidityDiff = false;
      }
    }
  }
};
</script>

<style scoped>
/* 탭 네비게이션 스타일 */
.tabs-wrapper {
  margin-bottom: 24px;
  border-bottom: 1px solid #e4e6ef;
}

.tabs-scroll-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.tabs-scroll-container::-webkit-scrollbar {
  display: none;
}

.tabs-navigation {
  display: flex;
  gap: 8px;
  min-width: min-content;
  padding-bottom: 2px;
}

.tab-button {
  flex-shrink: 0;
  padding: 12px 24px;
  border: none;
  background: transparent;
  color: #7e8299;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
  transition: color 0.2s;
}

.tab-button:hover {
  color: #3699ff;
}

.tab-button.active {
  color: #3699ff;
  font-weight: 600;
}

.tab-button.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #3699ff;
}

/* 탭 컨텐츠 영역 */
.tab-content-area {
  min-height: 400px;
}

.tab-panel {
  animation: fadeIn 0.2s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* 설정 그리드 */
.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

/* 설정 카드 */
.setting-card {
  background: #f3f6f9;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e4e6ef;
}

.setting-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.header-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-label {
  font-size: 13px;
  color: #7e8299;
  font-weight: 500;
  white-space: nowrap;
  margin: 0;
}

.header-input {
  width: 80px;
  text-align: center;
  font-size: 14px;
  padding: 6px 10px;
}

.setting-title {
  font-size: 16px;
  font-weight: 600;
  color: #181c32;
  margin: 0;
}

.setting-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 시간 범위 그룹 */
.time-range-group {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.time-input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 120px;
}

.time-label {
  font-size: 13px;
  color: #7e8299;
  font-weight: 500;
  margin: 0;
}

.time-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
}

.time-input {
  width: 70px;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  padding: 10px 8px;
}

.time-input::-webkit-inner-spin-button,
.time-input::-webkit-outer-spin-button {
  height: 30px;
  opacity: 1;
}

.time-separator {
  font-size: 18px;
  font-weight: 600;
  color: #7e8299;
}

.range-separator {
  font-size: 18px;
  font-weight: 600;
  color: #7e8299;
  padding-bottom: 8px;
  flex-shrink: 0;
}

/* 작동시간 그룹 */
.duration-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.duration-label {
  font-size: 13px;
  color: #7e8299;
  font-weight: 500;
  margin: 0;
}

/* 값 그룹 */
.value-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.value-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 120px;
}

.value-label {
  font-size: 13px;
  color: #7e8299;
  font-weight: 500;
  margin: 0;
}

/* 파라미터 그룹 */
.param-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.param-label {
  font-size: 13px;
  color: #7e8299;
  font-weight: 500;
  margin: 0;
}

/* 간격 그룹 */
.interval-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.interval-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 100px;
}

.interval-label {
  font-size: 13px;
  color: #7e8299;
  font-weight: 500;
  margin: 0;
}

/* 폼 컨트롤 스타일 */
.form-control {
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  border-color: #3699ff;
  outline: none;
  box-shadow: 0 0 0 3px rgba(54, 153, 255, 0.1);
}

.form-control:read-only {
  background-color: #f3f6f9;
  color: #7e8299;
  cursor: not-allowed;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  opacity: 1;
}

/* 모바일 반응형 */
@media (max-width: 768px) {
  .settings-grid {
    grid-template-columns: 1fr;
  }

  .setting-card {
    padding: 16px;
  }

  .time-range-group {
    flex-direction: column;
    align-items: stretch;
  }

  .time-input-group {
    min-width: auto;
  }

  .range-separator {
    padding-bottom: 0;
    text-align: center;
  }

  .tab-button {
    padding: 10px 20px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .setting-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-input-group {
    width: 100%;
    flex-wrap: wrap;
  }

  .header-input {
    flex: 1;
    min-width: 60px;
  }

  .setting-header .btn {
    width: 100%;
  }

  .time-inputs {
    justify-content: center;
  }
}
</style>
